import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { api, ApiError } from '@/api/client'
import { Check, X, Monitor, AlertCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'

type PairState = 'input' | 'loading' | 'confirm' | 'approved' | 'denied' | 'error'

interface DeviceInfo {
  device_name: string
  platform: string
  requested_at: string
  expires_at: string
}

export default function DevicePairPage() {
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [code, setCode] = useState(() => window.location.hash.replace('#', '').trim())
  const [state, setState] = useState<PairState>(code ? 'loading' : 'input')
  const [device, setDevice] = useState<DeviceInfo | null>(null)
  const [error, setError] = useState<string>()

  const lookup = useCallback(async (userCode: string) => {
    setState('loading')
    setError(undefined)
    try {
      const result = await api.request<DeviceInfo>(`/device-approvals/lookup?user_code=${encodeURIComponent(userCode)}`)
      setDevice(result)
      setState('confirm')
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Device code not found or expired. Please try again.')
      } else if (err instanceof ApiError && err.status === 410) {
        setError('This authorization has expired. Please generate a new code on your desktop app.')
      } else {
        setError('Something went wrong. Please try again.')
      }
      setState('error')
    }
  }, [])

  // Auto-lookup if code came from hash
  useEffect(() => {
    if (code && isAuthenticated && state === 'loading') {
      lookup(code)
    }
  }, [code, isAuthenticated, state, lookup])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (code.trim()) lookup(code.trim())
  }

  const handleApprove = async () => {
    if (!code) return
    setState('loading')
    try {
      await api.request('/device-approvals/approve', {
        method: 'POST',
        body: JSON.stringify({ user_code: code }),
      })
      setState('approved')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('This authorization has already been processed.')
      } else {
        setError('Failed to approve device. Please try again.')
      }
      setState('error')
    }
  }

  const handleDeny = async () => {
    if (!code) return
    setState('loading')
    try {
      await api.request('/device-approvals/deny', {
        method: 'POST',
        body: JSON.stringify({ user_code: code }),
      })
      setState('denied')
    } catch {
      setError('Failed to deny device. Please try again.')
      setState('error')
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    window.location.replace(`/login?redirect=${encodeURIComponent(`/pair#${code}`)}`)
    return null
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Branding */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Monitor size={28} />
          </div>
          <h1 className="text-xl tracking-tight">
            <span className="font-bold">Ledger.</span>{' '}
            <span className="text-lg font-light text-muted-foreground">Core</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Pair a desktop device</p>
        </div>

        {/* Code input */}
        {state === 'input' && (
          <Card>
            <CardContent className="pt-6">
              <p className="mb-4 text-sm text-muted-foreground">Enter the code shown on your desktop app.</p>
              <form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  maxLength={9}
                  className="mb-4 text-center font-mono text-lg tracking-widest"
                  autoFocus
                />
                <Button type="submit" className="w-full" disabled={!code.trim()}>
                  Look up device
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {state === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-12">
            <Spinner className="h-8 w-8 text-primary" />
            <p className="text-sm text-muted-foreground">Looking up device…</p>
          </div>
        )}

        {/* Confirm device */}
        {state === 'confirm' && device && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold">Approve this device?</h2>
              <div className="mb-6 space-y-3 rounded-lg bg-muted p-4">
                <div className="flex items-center gap-3">
                  <Monitor size={18} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{device.device_name}</p>
                    <p className="text-xs text-muted-foreground">{device.platform === 'win32' ? 'Windows' : device.platform === 'darwin' ? 'macOS' : device.platform}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Requested {new Date(device.requested_at).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleApprove} className="flex-1 gap-2">
                  <Check size={16} />
                  Approve
                </Button>
                <Button variant="outline" onClick={handleDeny} className="flex-1 gap-2">
                  <X size={16} />
                  Deny
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Approved */}
        {state === 'approved' && (
          <Card className="border-emerald-200 dark:border-emerald-800">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/20">
                <Check size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="mb-1 text-lg font-semibold">Device paired</h2>
              <p className="text-sm text-muted-foreground">Your desktop app is now connected. You can close this tab.</p>
            </CardContent>
          </Card>
        )}

        {/* Denied */}
        {state === 'denied' && (
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <X size={24} className="text-muted-foreground" />
              </div>
              <h2 className="mb-1 text-lg font-semibold">Device denied</h2>
              <p className="text-sm text-muted-foreground">The device will not be paired.</p>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {state === 'error' && (
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle size={24} className="text-destructive" />
              </div>
              <h2 className="mb-1 text-lg font-semibold">Error</h2>
              <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
              <Button variant="outline" onClick={() => { setState('input'); setError(undefined) }}>
                Try again
              </Button>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  )
}
